// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { DoubleMatrix, ExpandDoubleVector } from './DoubleMatrix';
import { PTLIndicator } from './Indicator';
import { Periods, TFInfo } from '@shared/utils/History/TFInfo';
import { DynProperty } from '../../DynProperty';
import { CustomEvent } from '@shared/utils/CustomEvents';
// Базовый встроенный индикатор
export class iBuildInIndicator {
    constructor(linesCount) {
        this.OnError = new CustomEvent();
        this.timeFrameInfo = new TFInfo();
        this.FParent = null;
        this.FPriceType = PriceType.Open;
        this.FSymbol = '';
        this.FCount = 0;
        this.lastCalculationOffset = 0;
        this.mainChart = null;
        this._disposed = false;
        this.FUseCalcOnArray = false;
        this.FArrayReversal = false;
        // место хранения данных, на основе которых
        // считается значение индикатора
        this.FPriceData = null;
        this.offset = 0;
        this.args = null;
        linesCount = linesCount === undefined ? 1 : linesCount;
        const FData = (this.FData = new DoubleMatrix(linesCount));
        for (let i = 0; i < linesCount; i++) {
            FData.set(i, new ExpandDoubleVector());
        }
    }
    get Parent() {
        return this.FParent;
    }
    set Parent(value) {
        this.FParent = value;
    }
    // Имя индикатора - ключ создания
    get Name() {
        return 'iBaseBuildInIndicator';
    }
    set Name(value) { }
    // Период индикатора
    get Period() {
        return this.timeFrameInfo.Periods;
    }
    // Цена, по которой будет построен данный индикатор
    get PriceType() {
        return this.FPriceType;
    }
    set PriceType(value) {
        this.FPriceType = value;
    }
    // Символ, для которого производится расчет
    get Symbol() {
        return this.FSymbol;
    }
    set Symbol(value) {
        this.FSymbol = value;
    }
    // TODO. Ugly.
    get DefaultKey() {
        return this.Name + this.Symbol + this.Period;
    }
    get Key() {
        throw new Error();
    }
    // Тип истории, по которой индикатор будет работать.
    get HistoryType() {
        return this.timeFrameInfo.HistoryType;
    }
    // Список с данными
    get Data() {
        return this.FData;
    }
    get MainChart() {
        return this.mainChart;
    }
    set MainChart(value) {
        this.mainChart = value;
    }
    get CalculationStartIndex() {
        this.lastCalculationOffset = PTLIndicator.GetStartIndex(this.FParent, this.mainChart);
        return this.lastCalculationOffset;
    }
    get TimeFrameInfo() {
        return this.timeFrameInfo;
    }
    set TimeFrameInfo(value) {
        this.timeFrameInfo = value;
    }
    get Count() {
        return this.FCount;
    }
    // Вычисления индикатора по массиву
    get PriceData() {
        return this.FPriceData;
    }
    set PriceData(value) {
        this.FUseCalcOnArray = !!value;
        if (this.FUseCalcOnArray) {
            this.FPriceData = value;
        }
    }
    get ArrayReversal() {
        return this.FArrayReversal;
    }
    set ArrayReversal(value) {
        this.FArrayReversal = value;
    }
    get SeparateWindow() {
        return true;
    }
    get RecalculateRange() {
        const startIdx = PTLIndicator.GetStartIndex(this.FParent, this.mainChart);
        return this.lastCalculationOffset > startIdx;
    }
    SetLineShift(line, shift) {
        const v = this.FData.get(line);
        if (v) {
            v.Shift = shift;
        }
    }
    _GetValue(line, index) {
        const v = this.FData.get(line);
        const length = v.Length - 1;
        return v.get(length - index + (v.Shift || 0));
    }
    // TODO. Ugly.
    GetValue(par0, par1) {
        if (par0 !== undefined && par1 !== undefined) {
            return this.getValueByLineAndIndex(par0, par1);
        }
        if (par0 !== undefined && par1 === undefined) {
            return this.getValueByIndex(par0);
        }
        if (par0 === undefined && par1 === undefined) {
            return this.getValue();
        }
        throw new Error();
    }
    getValueByLineAndIndex(line, index) {
        const v = this.FData.get(line);
        const length = v.Length - 1;
        return v.get(length - index);
    }
    getValueByIndex(index) {
        const v = this.FData.get(0);
        const length = v.Length - 1;
        return v.get(length - index);
    }
    getValue() {
        const v = this.FData.get(0);
        const length = v.Length;
        return v.get(length - 1);
    }
    SetValue(par0, par1, par2) {
        if (par0 !== undefined && par1 !== undefined && par2 !== undefined) {
            this.setValueByLineAndIndex(par0, par1, par2);
            return;
        }
        if (par0 !== undefined && par1 !== undefined && par2 === undefined) {
            this.setValueByIndex(par0, par1);
            return;
        }
        if (par0 !== undefined && par1 === undefined && par2 === undefined) {
            this.setValue(par0);
            return;
        }
        throw new Error();
    }
    setValueByLineAndIndex(line, index, value) {
        const v = this.FData.get(line);
        const i = v.Length - 1 - index;
        if (i >= 0 && i < v.Length) {
            v.set(i, value);
        }
    }
    setValueByIndex(index, value) {
        const v = this.FData.get(0);
        v.set(v.Length - 1 - index, value);
    }
    setValue(value) {
        const v = this.FData.get(0);
        v.set(v.Length - 1, value);
    }
    Refresh(par0, par1) {
        if (par0 !== undefined && par1 !== undefined) {
            this.refreshByCount(par0, par1);
            return;
        }
        if (par0 !== undefined && par1 === undefined) {
            this.refresh(par0);
            return;
        }
        throw new Error();
    }
    refreshByCount(count, newThread) {
        this.Refresh_Action(count);
    }
    refresh(newThread) {
        if (this.FParent) {
            this.refreshByCount(this.FParent.Count(), newThread);
        }
    }
    Refresh_Action(count) {
        if (this._disposed) {
            return;
        }
        const offset = this.CalculationStartIndex;
        this.SetOffset(offset);
        this.FCount = 0;
        for (let i = offset; i < count; i++) {
            this.NextBar(true);
            this.OnQuote(this.FParent, true);
        }
    }
    SetOffset(offset) {
        const FData = this.FData;
        for (let i = 0, len = FData.Length; i < len; i++) {
            const vector = FData.get(i); /* as ExpandDoubleVector */
            /*
        ExpandDoubleVectorFile ov = vector as ExpandDoubleVectorFile;
        if (ov != null)
        {
            ov.Clear();
            ov.Offset = offset;
        }
        else */ if (vector) {
                vector.Length = 0;
            }
        }
    }
    NextBar(callBound) {
        if (this._disposed) {
            return;
        }
        const FData = this.FData;
        for (let i = 0, len = FData.Length; i < len; i++) {
            const vector = FData.get(i); /* as ExpandDoubleVector */
            if (vector)
                vector.Add(0);
        }
        // cache count
        this.FCount = FData.get(0).Length;
    }
    OnQuote(ci, callBound, callFromRefresh = false) { }
    Init(async) {
        return true;
    }
    Complete() { }
    GetPrice(price, index) {
        const FCount = this.FCount;
        return this.FUseCalcOnArray
            ? this.FPriceData.get(FCount - 1 - index)
            : this.FParent.GetByType(FCount - 1 - index, this.Period !== Periods.TIC ? price : PriceType.Close);
    }
    GetVolume(index) {
        return this.FParent.GetVolume(this.FCount - 1 - index);
    }
    Dispose() {
        this._disposed = true;
        const FData = this.FData;
        if (FData.Dispose) {
            FData.Dispose();
        }
        this.FPriceData = null;
        this.mainChart = null;
    }
    Properties() {
        const properties = [];
        const args = this.args;
        if (!args) {
            return properties;
        }
        for (let i = 0, len = args.length; i < len; i++) {
            const prop = new DynProperty('Value' + i, args[i], DynProperty.DOUBLE, '');
            properties.push(prop);
        }
        return properties;
    }
    callBack(properties) {
        if (!properties) {
            return;
        }
        const args = this.args;
        for (let i = 0, len = properties.length; i < len; i++) {
            const dynProp = DynProperty.getPropValue(properties, 'Value' + i);
            args[i] = dynProp.value;
        }
    }
}
